import { createStore } from "vuex";
import axios from "axios";
import Socket from "@/socket"
import {differenceInDays, endOfMonth, startOfMonth, subDays, subMonths} from 'date-fns'
import { endOfDay, isAfter, isBefore } from "date-fns/esm";
// import { calculateResult, getStatus } from "../commons/helpers";

const API = axios.create({
  baseURL:
    process.env.NODE_ENV === "development"
      ? undefined
      : "https://portal-api.betslip.tips",
});


export default createStore({
  state: function() {
 
    return {
      user: {
        sessionToken: undefined,
        currentTips: [],
        history: [],
        summary: {},
        result: undefined,
        loggedIn: false,
        user: {},
      },
      settings: {} ,
      timeframe: '10DAYS',
      tokenUpdated: false,
      theme:
        localStorage.theme === "dark" ||
        (!("theme" in localStorage) &&
          window.matchMedia("(prefers-color-scheme: dark)").matches)
          ? "dark"
          : "light",
      liveData: {},
      goalToast: null,
      accumulators: {
        today: null,
        history: [],
        stats: null,
        spot: []
      },
      isAdmin: false,
    };
  },
  getters: {
    user(state) {
      return state.user;
    },

    tips(state) {
      return (
        state.history?.filter(tip => {
          let createdDate = new Date(tip.createdDate)
          let startDate, endDate
          switch (state.timeframe) {
            case 'THISMONTH':
              startDate = startOfMonth(new Date());
              endDate = endOfDay(new Date())
              break;
            case 'PREVMONTH':
              startDate = startOfMonth(subMonths(new Date(),1))
              endDate = endOfMonth(startDate)
              break;
            case '10DAYS':
            default:
                startDate = subDays(new Date(), 10);
                endDate = endOfDay(new Date())
                break;  
          }
          return isAfter(createdDate, startDate) && isBefore(createdDate, endDate)
        }).map((tip) => {
          let status
          
          if (!isNaN(tip.status)) {
            switch (tip.status) {
              case 0:
                status = "PENDING";
                break;
              case 1:
                status = "SUCCESS";
                break;
              case 2:
                status = "FAIL";
                break;
              case 3:
                status = "VOID";
                break;
              case 4:
                status = "HALFWIN";
                break;
              case 5:
                status = "HALFLOSS";
                break;
            }
          }
          return {
            ...tip,
            status,
       //     virtualEquityAfterTip: virtualEquityAfterTip ? virtualEquityAfterTip : 0

          };
        }) || []
      );
    },
    summary(state, getters) {
      let val = getters.tips || []
      return val.reduce((res,tip) => {
        
        switch (tip.status) {
          case "PENDING":
            break;
          case "SUCCESS":
            res.wins++
            break;
          case "FAIL":
            res.losses++
            break;
          case "VOID":
            res.voids++
            break;
          case "HALFWIN":
            res.halfWins++
            break;
          case "HALFLOSS":
            res.halfLosses++
            break;
        }
        return res;
      }, {
        halfLosses: 0,
        halfWins: 0,
        losses: 0,
        voids: 0,
        wins: 0  
      });
    },
    settings(state) {
      return state.settings || {};
    },
    tipsByDate(state, getters) {
      return getters.tips?.reduce((groups, game) => {
        const date = game.createdDate.split("T")[0];
        if (!groups[date]) {
          groups[date] = [];
        }
        groups[date].push(game);
        return groups;
      }, {}) || {}
    },
    equityByDate(state, getters) {
      // Compute equity from settlement outcomes rather than trusting
      // virtualEquityAfterTip — that field is written at tip-creation
      // (stake deducted, no winnings added) and never updated when the
      // tip settles, which under-reports the equity on wins by stake*odds.
      // Apply the canonical outcome formulas per status:
      //   WIN    : equity += stake * (odds - 1)
      //   LOSS   : equity -= stake
      //   VOID   : equity += 0
      //   HALFWIN: equity += stake/2 * (odds - 1)
      //   HALFLO : equity -= stake/2
      const settlementDelta = (status, stake, odds) => {
        if (!stake || !odds) return 0;
        switch ((status || "").toUpperCase()) {
          case "SUCCESS":
          case "WIN":      return stake * (odds - 1);
          case "FAIL":
          case "LOSS":     return -stake;
          case "VOID":     return 0;
          case "HALFWIN":  return (stake / 2) * (odds - 1);
          case "HALFLOSS": return -stake / 2;
          default:         return 0;
        }
      };

      const tipsByDate = getters.tipsByDate;
      let runningEquity = (state.settings && state.settings.startingEquity) || 0;
      // Seed from the first settled tip's virtualEquityAfterTip + its own
      // stake to recover the "before-first-tip" equity. Falls back to
      // settings.startingEquity if the first tip lacks the field.
      const allDays = Object.keys(tipsByDate).sort((a, b) => (a > b ? 1 : -1));
      for (const d of allDays) {
        const settled = (tipsByDate[d] || []).filter((i) => i.status !== "PENDING");
        if (!settled.length) continue;
        const first = settled.sort((a, b) => (a.createdDate > b.createdDate ? 1 : -1))[0];
        const seed = first.virtualEquityAfterTip;
        const firstStake = parseFloat(first.virtualTipAmount) || 0;
        if (seed != null && firstStake > 0) {
          // virtualEquityAfterTip is set at tip-creation = pre-tip equity minus stake
          runningEquity = parseFloat(seed) + firstStake;
        }
        break;
      }

      const dataPoints = allDays.map((d) => {
        const dayTips = [...(tipsByDate[d] || [])];
        const tipsOutcome = {};
        const filtered = dayTips.filter((i) => i.status !== "PENDING");

        const sorted = filtered.sort((a, b) => (a.createdDate > b.createdDate ? 1 : -1));
        for (const item of sorted) {
          const stake = parseFloat(item.virtualTipAmount) || 0;
          const odds = parseFloat(item.oddValue) || 0;
          const delta = settlementDelta(item.status, stake, odds);
          const before = runningEquity;
          runningEquity = before + delta;
          const percent = before ? runningEquity / before - 1 : 0;
          const symbol = delta === 0 ? "" : delta > 0 ? "▴" : "▾";
          tipsOutcome[item.id] = {
            value: delta,
            stake,
            total: runningEquity,
            percent,
            symbol,
          };
        }

        return {
          _length: filtered.length,
          x: new Date(d),
          y: runningEquity,
          tipsOutcome,
        };
      });

      return dataPoints.filter((d) => d._length).sort((a, b) => (a.x > b.x ? 1 : -1));
    },
    getLiveData(state) {
      return (id) => state.liveData?.[id.toString()]
    }
  },
  mutations: {
    setAccessToken: (state, token) => {
      state.sessionToken = token;
    },
    setAdmin: (state, isAdmin) => {
      state.isAdmin = !!isAdmin;
    },
    toggleTheme: function(state) {
      state.theme = state.theme === "light" ? "dark" : "light";
      localStorage.theme = state.theme;
      if (state.theme === "light") {
        document.querySelector("html").classList.remove("dark");
      } else {
        document.querySelector("html").classList.add("dark");
      }
    },
    setHistory: (state, history) => {
      if (history && history.length) {
        state.history = history;
      }
    },
    // setSummary: (state, summary) => {
    //   state.summary = summary;
    // },
    setUser: function(state, user) {
      state.user = user;
    },
    setSettings: (state, settings) => {
      state.settings = settings
    },
    tokenUpdated: function(state){
      state.tokenUpdated = true
    },
    setLiveData: function(state, data) {
      let gameId = data.gameId.toString()
      let prev = state.liveData[gameId]

      // Detect goal scored
      if (prev) {
        let prevGoals = (prev.homeGoals || 0) + (prev.awayGoals || 0)
        let newGoals = (data.homeGoals || 0) + (data.awayGoals || 0)
        if (newGoals > prevGoals) {
          state.goalToast = {
            homeTeam: data.homeTeam,
            awayTeam: data.awayTeam,
            homeGoals: data.homeGoals,
            awayGoals: data.awayGoals,
            minute: data.minute,
            timestamp: Date.now()
          }
        }
      }

      let obj = { ...state.liveData }
      obj[gameId] = data
      state.liveData = obj
    },
    clearGoalToast: function(state) {
      state.goalToast = null
    },
    setTodaysAcca(state, acca) {
      state.accumulators.today = acca
    },
    setAccumulatorHistory(state, list) {
      state.accumulators.history = list || []
    },
    setAccumulatorStats(state, stats) {
      state.accumulators.stats = stats
    },
    setSpotAccas(state, list) {
      state.accumulators.spot = list || []
    },
    setTimeframe(state, val) {
      console.log('timeframe', val)
      state.timeframe = val
    }

  },
  actions: {
    getSettings: async function (context) {
      let token = context.state.sessionToken;
      return API.get("/api/user/settings", {
        headers: {
          Authorization: "Bearer " + token
        }
      })
        .then(res => {
          context.commit("setSettings", res.data);
        })
        .catch(e => {
          console.log("FAILED Settings");
          console.log(e);
        });
    },
    updateSettings: async function(context, data){
      let token =  context.state.sessionToken;
      return API.put(`/api/User/Settings`, data, {
        headers: {
          Authorization: "Bearer " + token,
          "Content-Type": "application/json"
        },
      }).then(() => {
        return context.dispatch('getSettings')
      })
      
    },
    resetEquity: async function(context){
      
      return API.post(`/api/Virtual/Reset?amount=${context.state?.settings?.startingEquity}`, undefined, {
        headers: {
          Authorization: "Bearer " + context.state.sessionToken,
          "Content-Type": "application/json"
        },
      }).then(() => {
        return context.dispatch('getSettings')
      })
      
    },
    getHistory: function(context) {
      // Pull tips since the user's startingEquity setupDate so the equity chart
      // can render from the actual beginning of their tracked period. Falls back
      // to 365 days when setupDate is missing or implausible.
      const now = new Date();
      let days = 365;
      const setupDate = context.state.settings && context.state.settings.setupDate;
      if (setupDate) {
        const since = Math.max(1, differenceInDays(now, new Date(setupDate)) + 1);
        // Cap to ~3 years to keep payload sane on long-lived accounts
        days = Math.min(1095, since);
      }

      return API.get(`/api/User/Tips?days=${days}`, {
        headers: {
          Authorization: "Bearer " + context.state.sessionToken,
        },
      })
        .then((res) => {
          context.commit("setHistory", res.data);
        })
        .catch((e) => {
          console.log("FAILED HISTORY");
          console.log(e);
        });
    },
    getSummary: function() {
      // return API.get("/api/User/Summary", {
      //   headers: {
      //     Authorization: "Bearer " + context.state.sessionToken,
      //   },
      // })
      //   .then((res) => {
      //     context.commit("setSummary", res.data);
      //   })
      //   .catch((e) => {
      //     console.log("FAILED SUMMARY");
      //     console.log(e);
      //   });
    },
    updateDevice: async function(context, data) {
      console.log("---update device---");
      let token = context.state.sessionToken;
      return API.post(`/api/User`, data, {
        headers: {
          Authorization: "Bearer " + token,
        },
      }).catch((e) => {
        console.log("FAILED UPDATE DEVICE");
        console.log(e.response.data);
      });
    },
    updateDeviceToken: async function(context, data) {
      let token = context.state.sessionToken;
      if (!context.state.tokenUpdated){
        return API.put(`/api/User/DeviceToken`, data, {
          headers: {
            Authorization: "Bearer " + token,
          },
        }).then(() => {
          context.commit('tokenUpdated')
        }).catch((e) => {
          console.log("FAILED UPDATE DEVICE");
          console.log(e.response.data);
        });
      }
    },
    getLiveData: function(context){
      Socket().addEventListener('message',  (event) => {
        context.commit('setLiveData', JSON.parse(event.data.toString()))
      });

    },
    getTodaysAcca: async function(context) {
      let token = context.state.sessionToken;
      try {
        const res = await API.get("/api/Accumulator/Today", {
          headers: { Authorization: "Bearer " + token }
        });
        context.commit("setTodaysAcca", res.data);
      } catch (e) {
        if (e.response && e.response.status === 404) {
          context.commit("setTodaysAcca", null);
        } else {
          console.error("getTodaysAcca failed", e);
        }
      }
    },
    getAccumulatorHistory: async function(context, days = 30) {
      let token = context.state.sessionToken;
      try {
        const res = await API.get(`/api/Accumulator/History?days=${days}`, {
          headers: { Authorization: "Bearer " + token }
        });
        context.commit("setAccumulatorHistory", res.data);
      } catch (e) {
        console.error("getAccumulatorHistory failed", e);
      }
    },
    getSpotAccaHistory: async function(context, limit = 10) {
      let token = context.state.sessionToken;
      try {
        const res = await API.get(`/api/Accumulator/Spot/Recent?limit=${limit}`, {
          headers: { Authorization: "Bearer " + token }
        });
        context.commit("setSpotAccas", res.data);
      } catch (e) {
        console.error("getSpotAccaHistory failed", e);
      }
    },
    getAccumulatorById: async function(context, id) {
      let token = context.state.sessionToken;
      try {
        const res = await API.get(`/api/Accumulator/${id}`, {
          headers: { Authorization: "Bearer " + token }
        });
        return res.data;
      } catch (e) {
        console.error("getAccumulatorById failed", e);
        return null;
      }
    },
    getAccumulatorStats: async function(context, days = 30) {
      let token = context.state.sessionToken;
      try {
        const res = await API.get(`/api/Accumulator/Stats?days=${days}`, {
          headers: { Authorization: "Bearer " + token }
        });
        context.commit("setAccumulatorStats", res.data);
      } catch (e) {
        console.error("getAccumulatorStats failed", e);
      }
    },
    generateApiKey: async function(context) {
      let token = context.state.sessionToken;
      return API.post("/api/User/ApiKey", undefined, {
        headers: {
          Authorization: "Bearer " + token,
        },
      });
    },
    getApiKey: async function(context) {
      let token = context.state.sessionToken;
      return API.get("/api/User/ApiKey", {
        headers: {
          Authorization: "Bearer " + token,
        },
      });
    },
    revokeApiKey: async function(context) {
      let token = context.state.sessionToken;
      return API.delete("/api/User/ApiKey", {
        headers: {
          Authorization: "Bearer " + token,
        },
      });
    },
    getOpenClawStatus: async function(context) {
      let token = context.state.sessionToken;
      return API.get("/api/User/ApiKey/Status", {
        headers: {
          Authorization: "Bearer " + token,
        },
      });
    },
  },
});
